import { useState, useEffect, useCallback, useMemo } from 'react';
import localforage from 'localforage';

const taskModificationsStore = localforage.createInstance({
  name: 'TaskModifications',
});

export const TASK_MOD_SYNC_STATES = {
  PENDING: 'pending',
  SENT: 'sent',
  CONFIRMED: 'confirmed',
};

const isRepeatableTask = (task) => {
  return task?.type?.code === 'INFOV' || task?.categoryCode === 'DIS';
};

export const getTaskModificationSyncState = (mod = {}) => {
  if (mod.serverConfirmedAt) {
    return TASK_MOD_SYNC_STATES.CONFIRMED;
  }
  if (mod.syncState) {
    return mod.syncState;
  }
  if (mod.synced) {
    return TASK_MOD_SYNC_STATES.SENT;
  }
  return TASK_MOD_SYNC_STATES.PENDING;
};

export const isTaskModificationAwaitingConfirmation = (mod = {}) => {
  return getTaskModificationSyncState(mod) !== TASK_MOD_SYNC_STATES.CONFIRMED;
};

const getTaskConfirmationRule = (task, mod = {}) => {
  if (mod.confirmation) {
    return mod.confirmation;
  }

  if (mod.status?.code) {
    return {
      kind: 'status',
      statusCode: mod.status.code,
      final: mod.status.final || null,
    };
  }

  if (mod.executionCount !== undefined && isRepeatableTask(task)) {
    return {
      kind: 'executionCount',
      minExecutionCount:
        Number(mod.baseExecutionCount || 0) + Number(mod.executionCount || 0),
    };
  }

  return null;
};

export const isTaskModificationConfirmedByServer = (task, mod = {}) => {
  if (!task || !mod) {
    return false;
  }

  const confirmation = getTaskConfirmationRule(task, mod);

  if (confirmation?.kind === 'status') {
    if (confirmation.statusCode) {
      return task?.status?.code === confirmation.statusCode;
    }
    if (confirmation.final) {
      return task?.status?.final === confirmation.final;
    }
    return false;
  }

  if (confirmation?.kind === 'executionCount') {
    return (
      Number(task?.executionCount || 0) >=
      Number(confirmation.minExecutionCount || 0)
    );
  }

  if (mod.status?.final === 'D') {
    return task?.status?.final === 'D';
  }

  if (mod.executionCount !== undefined && isRepeatableTask(task)) {
    return Number(task?.executionCount || 0) > 0;
  }

  return false;
};

export const clearConfirmedTaskModificationsFromStorage = async (
  serverTasks = [],
) => {
  try {
    const currentMods =
      (await taskModificationsStore.getItem('modifications')) || {};
    const newMods = {};
    let cleared = 0;
    const serverTaskMap = new Map(
      (serverTasks || []).map((task) => [String(task.id), task]),
    );

    for (const [taskId, mod] of Object.entries(currentMods)) {
      const serverTask = serverTaskMap.get(String(taskId));
      if (isTaskModificationConfirmedByServer(serverTask, mod)) {
        cleared++;
      } else {
        newMods[taskId] = mod;
      }
    }

    if (cleared > 0) {
      await taskModificationsStore.setItem('modifications', newMods);
    }
  } catch (error) {
    console.error(
      '[TaskModifications] Error clearing confirmed task modifications:',
      error,
    );
  }
};

export const markTaskModificationsAsSentByOperationId = async (
  operationId,
  predicate = () => true,
) => {
  try {
    const currentMods =
      (await taskModificationsStore.getItem('modifications')) || {};
    const now = new Date().toISOString();
    let changed = false;

    for (const [taskId, mod] of Object.entries(currentMods)) {
      if (mod.pendingOperationId !== operationId || !predicate(mod, taskId)) {
        continue;
      }

      currentMods[taskId] = {
        ...mod,
        syncState: TASK_MOD_SYNC_STATES.SENT,
        synced: true,
        sentAt: now,
        syncedAt: now,
        pendingOperationId: null,
      };
      changed = true;
    }

    if (changed) {
      await taskModificationsStore.setItem('modifications', currentMods);
    }
  } catch (error) {
    console.error(
      '[TaskModifications] Error marking modifications as sent by operation:',
      error,
    );
  }
};

// Backward compatibility alias
export const clearRasterModificationsFromStorage =
  clearConfirmedTaskModificationsFromStorage;

export const clearOrderBasedTaskModificationsFromStorage =
  clearConfirmedTaskModificationsFromStorage;

const buildSyncMetadata = (currentMod = {}, options = {}, timestamp) => {
  const syncState =
    options.syncState || getTaskModificationSyncState(currentMod);
  const isSent = syncState === TASK_MOD_SYNC_STATES.SENT;
  const sentAt = isSent
    ? options.sentAt ||
      currentMod.sentAt ||
      currentMod.syncedAt ||
      timestamp
    : null;

  return {
    syncState,
    synced: isSent,
    sentAt,
    syncedAt: sentAt,
    pendingOperationId:
      options.pendingOperationId !== undefined
        ? options.pendingOperationId
        : currentMod.pendingOperationId || null,
    confirmation:
      options.confirmation !== undefined
        ? options.confirmation
        : currentMod.confirmation || null,
    completionSource:
      options.completionSource !== undefined
        ? options.completionSource
        : currentMod.completionSource || null,
    completedAt:
      options.completedAt !== undefined
        ? options.completedAt
        : currentMod.completedAt || null,
    baseExecutionCount:
      options.baseExecutionCount !== undefined
        ? options.baseExecutionCount
        : currentMod.baseExecutionCount,
    serverConfirmedAt: null,
  };
};

export const mergeTaskWithLocalModification = (task, mod) => {
  if (!task || !task.id || !mod) {
    return task;
  }

  const merged = { ...task };
  const awaitingConfirmation = isTaskModificationAwaitingConfirmation(mod);

  if (mod.status) {
    merged.status = {
      ...task.status,
      ...mod.status,
    };

    const executionCount =
      (task.executionCount || 0) + (mod.executionCount || 0);
    const hasExecutions = isRepeatableTask(task) && executionCount > 0;

    merged.isCompleted = mod.status.final === 'D' || hasExecutions;
    merged._locallyModified = true;
    merged._pendingSync = awaitingConfirmation;
    merged._syncState = getTaskModificationSyncState(mod);
  }

  if (mod.execution) {
    merged._localExecution = mod.execution;
    merged._locallyModified = true;
    if (awaitingConfirmation) {
      merged._pendingSync = true;
    }
    merged._syncState = getTaskModificationSyncState(mod);
  }

  if (mod.executionCount !== undefined) {
    merged.executionCount = (task.executionCount || 0) + mod.executionCount;
    if (!mod.status && isRepeatableTask(task) && merged.executionCount > 0) {
      merged.isCompleted = true;
      merged._locallyModified = true;
      merged._pendingSync = awaitingConfirmation;
      merged._syncState = getTaskModificationSyncState(mod);
    }
  }

  if (mod.executionTimes && mod.executionTimes.length > 0) {
    merged._localExecutionTimes = mod.executionTimes;
  }

  if (Array.isArray(mod.fulfilledProductIds) && mod.fulfilledProductIds.length > 0) {
    merged._fulfilledProductIds = mod.fulfilledProductIds.map(String);
  }

  return merged;
};

/**
 * Hook to manage local task modifications for offline support
 *
 * Stores local changes to tasks (status, execution data) that haven't been synced yet.
 * These modifications overlay on top of server data when displaying tasks.
 *
 * Structure:
 * {
 *   [taskId]: {
 *     status: { code, final, description },
 *     statusChangedAt: timestamp,
 *     execution: { text, number, photoData },
 *     pendingOperationId: string (links to offline queue)
 *   }
 * }
 */
export const useTaskModifications = () => {
  const [modifications, setModifications] = useState({});
  const [isLoaded, setIsLoaded] = useState(false);

  // Load modifications from localforage on mount
  useEffect(() => {
    const loadModifications = async () => {
      try {
        const stored = await taskModificationsStore.getItem('modifications');
        setModifications(stored || {});
      } catch (error) {
        console.error('[TaskModifications] Error loading:', error);
        setModifications({});
      } finally {
        setIsLoaded(true);
      }
    };
    loadModifications();
  }, []);

  // Persist modifications to localforage
  const persistModifications = useCallback(async (newModifications) => {
    try {
      await taskModificationsStore.setItem('modifications', newModifications);
      setModifications(newModifications);
    } catch (error) {
      console.error('[TaskModifications] Error persisting:', error);
    }
  }, []);

  const updateTaskStatus = useCallback(
    async (taskId, newStatus, pendingOperationId, options = {}) => {
      const taskIdStr = String(taskId);
      const currentMods =
        (await taskModificationsStore.getItem('modifications')) || {};
      const timestamp = new Date().toISOString();
      const currentMod = currentMods[taskIdStr] || {};
      const newModifications = {
        ...currentMods,
        [taskIdStr]: {
          ...currentMod,
          status: newStatus,
          statusChangedAt: timestamp,
          ...buildSyncMetadata(
            currentMod,
            {
              ...options,
              pendingOperationId,
              completedAt:
                options.completedAt !== undefined
                  ? options.completedAt
                  : newStatus?.final === 'D'
                    ? timestamp
                    : null,
            },
            timestamp,
          ),
        },
      };
      await persistModifications(newModifications);
    },
    [persistModifications],
  );

  const updateTaskExecution = useCallback(
    async (taskId, executionData, pendingOperationId, options = {}) => {
      const taskIdStr = String(taskId);
      const currentMods =
        (await taskModificationsStore.getItem('modifications')) || {};
      const timestamp = new Date().toISOString();
      const currentMod = currentMods[taskIdStr] || {};

      const currentExecutionCount = currentMod.executionCount || 0;
      const currentExecutionTimes = currentMod.executionTimes || [];

      const newExecutionCount = executionData.incrementCount
        ? currentExecutionCount + 1
        : currentExecutionCount;

      const newExecutionTimes = executionData.incrementCount
        ? [...currentExecutionTimes, new Date().toISOString()]
        : currentExecutionTimes;

      const newModifications = {
        ...currentMods,
        [taskIdStr]: {
          ...currentMod,
          execution: executionData,
          executionCount: newExecutionCount,
          executionTimes: newExecutionTimes,
          executionChangedAt: timestamp,
          ...buildSyncMetadata(
            currentMod,
            {
              ...options,
              pendingOperationId,
              completedAt:
                options.completedAt !== undefined
                  ? options.completedAt
                  : executionData.incrementCount
                    ? timestamp
                    : null,
            },
            timestamp,
          ),
        },
      };
      await persistModifications(newModifications);
    },
    [persistModifications],
  );

  /**
   * Clear modification for a task (call after successful sync)
   * @param {string|number} taskId
   */
  const clearTaskModification = useCallback(
    async (taskId) => {
      const taskIdStr = String(taskId);
      const { [taskIdStr]: removed, ...rest } = modifications;
      await persistModifications(rest);
      console.log('[TaskModifications] Cleared for task:', taskIdStr);
    },
    [modifications, persistModifications],
  );

  /**
   * Clear modification by operation ID (call when sync completes)
   * @param {string} operationId
   */
  const clearByOperationId = useCallback(
    async (operationId) => {
      const newModifications = { ...modifications };
      let cleared = false;

      for (const taskId of Object.keys(newModifications)) {
        if (newModifications[taskId].pendingOperationId === operationId) {
          delete newModifications[taskId];
          cleared = true;
          console.log(
            '[TaskModifications] Cleared by operationId:',
            operationId,
            'taskId:',
            taskId,
          );
        }
      }

      if (cleared) {
        await persistModifications(newModifications);
      }
    },
    [modifications, persistModifications],
  );

  /**
   * Get modification for a specific task
   * @param {string|number} taskId
   * @returns {object|null}
   */
  const getTaskModification = useCallback(
    (taskId) => {
      return modifications[String(taskId)] || null;
    },
    [modifications],
  );

  /**
   * Check if task has pending local modifications
   * @param {string|number} taskId
   * @returns {boolean}
   */
  const hasModification = useCallback(
    (taskId) => {
      return !!modifications[String(taskId)];
    },
    [modifications],
  );

  /**
   * Merge server task data with local modifications
   * Compares timestamps: if modification is newer than sync, apply it
   * If sync is newer, use server status as source of truth
   * @param {object} task - Server task data
   * @returns {object} - Merged task data
   */
  const mergeWithModifications = useCallback(
    (task) => {
      if (!task || !task.id) return task;

      const mod = modifications[String(task.id)];
      if (!mod) return task;

      return mergeTaskWithLocalModification(task, mod);
    },
    [modifications],
  );

  /**
   * Merge array of tasks with local modifications
   * @param {array} tasks
   * @returns {array}
   */
  const mergeTasksWithModifications = useCallback(
    (tasks) => {
      if (!Array.isArray(tasks)) return tasks;
      return tasks.map(mergeWithModifications);
    },
    [mergeWithModifications],
  );

  /**
   * Get all pending modifications count
   * @returns {number}
   */
  const pendingCount = useMemo(() => {
    return Object.keys(modifications).length;
  }, [modifications]);

  /**
   * Clear all modifications (use with caution)
   */
  const clearAllModifications = useCallback(async () => {
    await persistModifications({});
    console.log('[TaskModifications] All cleared');
  }, [persistModifications]);

  /**
   * Clear task modifications confirmed by a fresh server task fetch.
   */
  const clearOrderBasedTaskModifications = useCallback(
    async (serverTasks = []) => {
      await clearConfirmedTaskModificationsFromStorage(serverTasks);
      const stored = await taskModificationsStore.getItem('modifications');
      setModifications(stored || {});
    },
    [],
  );

  // Backward compatibility alias
  const clearRasterModifications = clearOrderBasedTaskModifications;

  return {
    isLoaded,
    modifications,
    pendingCount,
    updateTaskStatus,
    updateTaskExecution,
    clearTaskModification,
    clearByOperationId,
    getTaskModification,
    hasModification,
    mergeWithModifications,
    mergeTasksWithModifications,
    clearAllModifications,
    clearOrderBasedTaskModifications,
    clearRasterModifications, // Backward compatibility alias
  };
};

// Status definitions for local use (must match database mt_task_status table)
// Include both 'name' and 'description' for compatibility with server statuses
export const LOCAL_TASK_STATUSES = {
  NEW: { code: 'NEW', final: 'N', name: 'Novi', description: 'Novi' },
  IN_PROGRESS: {
    code: 'IN_PROGRESS',
    final: 'N',
    name: 'U toku',
    description: 'U toku',
  },
  IN_TIME_FINAL: {
    code: 'IN_TIME_FINAL',
    final: 'D',
    name: 'Završen u roku',
    description: 'Završen na vrijeme',
  },
  AFTER_TIME_FINAL: {
    code: 'AFTER_TIME_FINAL',
    final: 'D',
    name: 'Završen',
    description: 'Završen',
  },
  CANCELED: {
    code: 'CANCELED',
    final: 'D',
    name: 'Otkazano',
    description: 'Otkazano',
  },
  REJECTED: {
    code: 'REJECTED',
    final: 'D',
    name: 'Odbijeno',
    description: 'Odbijeno',
  },
};

export default useTaskModifications;
